import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { VisualizationTextMarkupRenderer } from '@mentimeter/tiptap/visualization-text-markup-renderer';
import { DotsCluster } from '../DotsCluster';
import { TextFitGhost } from '../../components/text-fit-ghost/TextFitGhost';
import { CorrectnessIndicator } from '../../components/CorrectnessIndicator';
import { getDots, getFadedSegments, type SegmentedDot } from './getDots';
import { Score } from './Score';

interface OptionProps {
  label: string;
  scores: number | SegmentedDot[];
  scoreDisplayText: string;
  width: number;
  height: number;
  color: string;
  shouldRenderTextResizer:
    | false
    | { labelsContainerRef: React.RefObject<HTMLDivElement> };
  isCorrect: boolean;
  fontFamily: string;
  textColor: string;
  shouldShowCorrect: boolean;
  disableAnimations: boolean;
  shouldHideScores: boolean;
  dotRadius?: number;
}

const DOTS_FONT_SIZES = [
  '12px',
  '14px',
  '16px',
  '18px',
  '20px',
  '22px',
  '24px',
  '26px',
  '28px',
  '32px',
  '36px',
  '40px',
];

const LABEL_PADDING_TOP = 24;
export const LABEL_HEIGHT_PERCENTAGE = 0.3;
const LOW_OPACITY = 0.4;

export const Option = ({
  label,
  scores,
  scoreDisplayText,
  width,
  height,
  color,
  isCorrect,
  shouldRenderTextResizer,
  fontFamily,
  textColor,
  shouldShowCorrect,
  disableAnimations,
  dotRadius = 10,
  shouldHideScores,
}: OptionProps) => {
  if (width <= 0 || height <= 0) {
    return null;
  }

  const labelHeight = height * LABEL_HEIGHT_PERCENTAGE;
  const opacity = shouldShowCorrect && !isCorrect ? LOW_OPACITY : 1;

  return (
    <Box
      width={width}
      height={height}
      alignItems="center"
      justifyContent="center"
    >
      <DotsCluster
        radius={dotRadius}
        dots={shouldHideScores ? [] : getDots(scores, color)}
        fadedSegments={getFadedSegments(scores)}
        width={width}
        height={height - labelHeight}
        disableAnimations={disableAnimations}
        canvasOpacity={opacity}
      />

      <Box
        flexDirection="row"
        height={labelHeight}
        justifyContent="flex-start"
        alignItems="baseline"
        maxWidth="100%"
        pt={`${LABEL_PADDING_TOP}px`}
        extend={({ theme }) => ({
          borderTop: `1px solid ${theme.colors.neutralWeak}`,
        })}
      >
        <Score
          scoreDisplayText={scoreDisplayText}
          color={color}
          disableAnimations={disableAnimations}
          shouldHideScore={shouldHideScores}
        />
        <Box height={labelHeight - LABEL_PADDING_TOP} flex="1 0" mx="16px">
          {shouldRenderTextResizer && (
            <TextFitGhost
              text={label}
              fontSizes={DOTS_FONT_SIZES}
              width={width}
              height={labelHeight - LABEL_PADDING_TOP}
              finalFontSizeContainerRef={
                shouldRenderTextResizer.labelsContainerRef
              }
              style={{ lineHeight: 1.1 }}
              shouldResizeFontSizeContainerWhileIterating
            >
              {(ref) => (
                <Text aria-hidden="true" ref={ref} lineHeight="inherit">
                  {label}
                </Text>
              )}
            </TextFitGhost>
          )}
          <VisualizationTextMarkupRenderer
            editorContentProps={{
              style: {
                fontSize: 'inherit',
                fontFamily,
                color: textColor,
                lineHeight: 1.1,
                width: '100%',
                wordBreak: 'break-word',
              },
            }}
            content={label}
          />
        </Box>
        <CorrectnessIndicator
          isCorrect={isCorrect}
          shouldShow={shouldShowCorrect}
          immediate={false}
          size="2ch"
          mx="space1"
        />
      </Box>
    </Box>
  );
};
